import {
  ClickAwayListener,
  Fade,
  Tooltip as MuiTooltip,
  TooltipProps,
  styled,
  tooltipClasses,
} from "@mui/material";
import React, { useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile";

interface CustomTooltipProps extends Omit<TooltipProps, "title"> {
  message: React.ReactNode;
}

const StyledMuiTooltip = styled(({ className, ...props }: TooltipProps) => (
  <MuiTooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.colors.secondary[8],
    color: theme.colors.defaultBackground,
    width: "225px",
    maxWidth: "225px",
    padding: "8px 12px",
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    borderRadius: "8px",
    margin: "0 !important",
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "16px",
    letterSpacing: "0.24px",
    whiteSpace: "pre-line",
  },
}));

export const Tooltip = ({
  message,
  children,
  placement = "top",
  ...props
}: CustomTooltipProps): React.ReactElement => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const tooltipContent = (
    <StyledMuiTooltip
      TransitionComponent={Fade}
      disableFocusListener={isMobile}
      disableHoverListener={isMobile}
      disableTouchListener={isMobile}
      open={isMobile ? open : undefined}
      placement={placement}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 10],
              },
            },
          ],
        },
      }}
      title={message}
      {...props}
    >
      <span
        onClick={isMobile ? handleTooltipOpen : undefined}
        style={{ cursor: "pointer", display: "flex" }}
      >
        {children}
      </span>
    </StyledMuiTooltip>
  );

  if (isMobile) {
    return (
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <div>{tooltipContent}</div>
      </ClickAwayListener>
    );
  }

  return tooltipContent;
};
